<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  visit: { type: Object, required: true },
});

const emit = defineEmits(['click']);

const { t } = useI18n();

const STATUS_STYLES = {
  scheduled: {
    dot: 'bg-blue-500',
    badge: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  },
  confirmed: {
    dot: 'bg-green-500',
    badge:
      'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  },
  rescheduled: {
    dot: 'bg-amber-500',
    badge:
      'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  },
  completed: { dot: 'bg-n-slate-6', badge: 'bg-n-alpha-1 text-n-slate-10' },
  cancelled: {
    dot: 'bg-ruby-500',
    badge: 'bg-ruby-50 text-ruby-700 dark:bg-ruby-900/30 dark:text-ruby-300',
  },
  no_show: {
    dot: 'bg-orange-500',
    badge:
      'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  },
};

const VISIT_TYPE_ICON = {
  site_visit: 'i-lucide-map-pin',
  video_call: 'i-lucide-video',
  office_meeting: 'i-lucide-building-2',
};

const statusStyle = computed(
  () => STATUS_STYLES[props.visit.status] || STATUS_STYLES.scheduled
);
const visitTypeIcon = computed(
  () => VISIT_TYPE_ICON[props.visit.visit_type] || 'i-lucide-calendar'
);

// M26.15.8 — scheduled_at_ist_time is pre-formatted in IST by the backend
// (Nexceo::TimeFormatter); previously this parsed the raw UTC scheduled_at
// with the *browser's* local offset instead of IST.
const timeDisplay = computed(() => props.visit.scheduled_at_ist_time || '');

const leadName = computed(
  () => props.visit.lead?.name || `Lead #${props.visit.lead_id}`
);

const projectName = computed(() => props.visit.project?.name || '');
const agentName = computed(() => props.visit.assigned_agent?.name || '—');

const locationDisplay = computed(
  () => props.visit.location || props.visit.meeting_link || ''
);

const meetingUrl = computed(
  () => props.visit.meeting_url || props.visit.meeting_link || null
);

const isVideoCall = computed(() => props.visit.visit_type === 'video_call');
const meetFailed = computed(() => props.visit.meeting_status === 'failed');
const calFailed = computed(() => props.visit.calendar_sync_status === 'failed');
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    class="flex gap-3 p-3 bg-white dark:bg-n-solid-3 border border-n-weak rounded-xl hover:border-woot-400 hover:shadow-sm cursor-pointer transition-all group"
    @click="emit('click', visit)"
  >
    <!-- Time column -->
    <div class="flex flex-col items-center min-w-[52px] pt-0.5">
      <span class="text-sm font-semibold text-n-slate-12">{{
        timeDisplay
      }}</span>
      <span class="size-2 rounded-full mt-1.5" :class="[statusStyle.dot]" />
    </div>

    <!-- Content -->
    <div class="flex-1 min-w-0">
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0">
          <p class="text-sm font-medium text-n-slate-12 truncate">
            {{ leadName }}
          </p>
          <p v-if="projectName" class="text-xs text-n-slate-10 truncate">
            {{ projectName }}
          </p>
        </div>
        <span
          class="shrink-0 text-xs px-2 py-0.5 rounded-full font-medium"
          :class="statusStyle.badge"
        >
          {{ t(`NEXCEO.CALENDAR.STATUS.${visit.status}`) }}
        </span>
      </div>

      <!-- Meta row -->
      <div
        class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-n-slate-10"
      >
        <span class="flex items-center gap-1">
          <span class="size-3.5" :class="[visitTypeIcon]" />
          {{ t(`NEXCEO.CALENDAR.VISIT_TYPE.${visit.visit_type}`) }}
        </span>
        <span v-if="agentName !== '—'" class="flex items-center gap-1">
          <span class="i-lucide-user size-3.5" />
          {{ agentName }}
        </span>
        <span
          v-if="locationDisplay && !isVideoCall"
          class="flex items-center gap-1 truncate max-w-[160px]"
        >
          <span class="i-lucide-map-pin size-3.5 shrink-0" />
          {{ locationDisplay }}
        </span>
        <!-- Join Meet link -->
        <a
          v-if="isVideoCall && meetingUrl"
          :href="meetingUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1 text-woot-600 dark:text-woot-400 hover:underline shrink-0"
          @click.stop
        >
          <span class="i-lucide-video size-3.5" />
          Join Meet
        </a>
        <!-- Warning badges -->
        <span
          v-if="meetFailed || calFailed"
          class="flex items-center gap-1 text-amber-600 dark:text-amber-400"
          :title="
            meetFailed ? 'Google Meet link failed' : 'Calendar sync failed'
          "
        >
          <span class="i-lucide-alert-triangle size-3.5" />
        </span>
      </div>
    </div>
  </div>
</template>
